<script lang="ts" setup>
  import type { Region } from '~/core/types/regions'

  type Props = {
    items: (Partial<Region> & { thumb: string, description: string })[]
  }

  const props = defineProps<Props>()

  const firstDisplayedKey = ref(0)
  const displayedItems = ref(4)

  const style = computed(() => {
    const em = `${firstDisplayedKey.value * 0.375}em`
    return {
      transform: `translateX(calc(-${25 * firstDisplayedKey.value}% - ${em}))`,
    }
  })

  function getItemClasses(key: number) {
    if (key === firstDisplayedKey.value) {
      return `first item-${key}`
    } else if (key === firstDisplayedKey.value + displayedItems.value - 1) {
      return `last item-${key}`
    } else if (key > firstDisplayedKey.value && key < firstDisplayedKey.value + displayedItems.value) {
      return `item-${key}`
    } else {
      return `hidden item-${key}`
    }
  }

  function handlePrev() {
    if (firstDisplayedKey.value > 0) {
      firstDisplayedKey.value = firstDisplayedKey.value - 1
    }
  }

  function handleNext(): void {
    if (firstDisplayedKey.value < props.items.length - displayedItems.value) {
      firstDisplayedKey.value = firstDisplayedKey.value + 1
    }
  }

</script>

<template>
  <div class="c-regions-slider">
    <button
      class="prev"
      :disabled="firstDisplayedKey <= 0"
      @click="handlePrev"
    >
      <SvgIcon
        icon="chevron"
        :height="22"
      />
    </button>
    <div
      class="grid"
      :style="style"
    >
      <div
        v-for="(item, key) of items"
        :key="key"
        class="item"
        :class="getItemClasses(key)"
      >
        <slot
          name="item"
          :item="item"
        />
      </div>
    </div>
    <button
      class="next"
      :disabled="firstDisplayedKey >= items.length - displayedItems"
      @click="handleNext"
    >
      <SvgIcon
        icon="chevron"
        :height="22"
      />
    </button>
  </div>
</template>
<style scoped>

.c-regions-slider {
  position: relative;
  z-index: 0;
  display: flex;
  max-width: 100%;
  flex: 1 1 auto;
  flex-direction: row;

  --displayed-regions: 4;

  button {
    position: absolute;
    z-index: 2;
    display: flex;
    width: 64px;
    height: 64px;
    align-items: center;
    justify-content: center;
    background: transparent;

    &.next {
      top: calc(50% - 32px);
      right: -64px;
    }

    &.prev {
      top: calc(50% - 32px);
      left: -64px;

      .c-icon {
        transform: rotate(0.5turn);
      }
    }

    &[disabled] {
      opacity: 0.5;
    }
  }

  .grid {
    position: relative;
    z-index: 1;
    display: flex;
    max-width: 100%;
    flex-direction: row;
    grid-gap: 1.5em;
  }

  @media screen and (width >= 1024px) {
    .grid {
      flex: 1 0 100%;
      transition: transform 0.5s ease-in-out;

      .item {
        --gap: 1.5em * (var(--displayed-regions) - 1) / var(--displayed-regions);

        flex: 0 0 calc((100% / var(--displayed-regions)) - var(--gap));
        opacity: 1;
        transition: all 0.5s ease-in-out;
        visibility: visible;

        &.hidden {
          opacity: 0;
          visibility: hidden;
        }
      }
    }
  }

  @media screen and (width >= 1600px) {
    button {
      width: 80px;
      height: 80px;

      &.next {
        top: calc(50% - 40px);
        right: -80px;
      }

      &.prev {
        top: calc(50% - 40px);
        left: -80px;
      }
    }
  }
}
</style>
